import { useEffect, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import ProtectedRoute from '@/auth/components/ProtectedRoute';
import { useCachedMySubmissions } from '@/shared/hooks/useCachedApi';
import PageHeader from '@/shared/components/PageHeader';
import TermSelect from '@/shared/components/TermSelect';
import SubmissionSummaryStats from '@/submissions/components/SubmissionSummaryStats';
import SubmissionsList from '@/submissions/components/SubmissionsList';
import SubmissionDetailModal from '@/submissions/components/SubmissionDetailModal';
import { computeSubmissionSummaryStats } from '@/submissions/utils/submissionStats';
import type { Submission } from '@/submissions/types';

export default function MySubmissions() {
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);

  // Estado local, no compartido con "Perfil" (D36). La primera carga va sin term:
  // el default lo resuelve el backend (D34).
  const [term, setTerm] = useState<string | null>(null);

  const { data: payload, loading, error, refetch } = useCachedMySubmissions(
    undefined,
    term ?? undefined,
  );

  // Sincronizar el estado con lo que resolvió el backend en la primera carga
  useEffect(() => {
    if (term === null && payload?.term) setTerm(payload.term);
  }, [payload?.term, term]);

  // useMemo para que el array vacío no cambie de identidad en cada render y no invalide
  // el useMemo de las estadísticas
  const submissions = useMemo(() => payload?.submissions ?? [], [payload?.submissions]);
  const exerciseProgress = payload?.exerciseProgress;
  const availableTerms = payload?.availableTerms ?? [];

  const {
    successfulDistinct,
    progressEnabledPercent,
    progressCoursePercent,
    solvedEnabledLabel,
    solvedCourseLabel,
  } = useMemo(
    () => computeSubmissionSummaryStats(submissions, exerciseProgress),
    [submissions, exerciseProgress],
  );

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <PageHeader
        title="Mis Envíos"
        subtitle="Revisa el historial de todas tus soluciones enviadas."
      />
      {availableTerms.length > 0 && (
        <TermSelect
          terms={availableTerms}
          value={term ?? payload?.term ?? null}
          onChange={setTerm}
          disabled={loading}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <ProtectedRoute>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {header}
          <div className="animate-pulse">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded" />
              ))}
            </div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-danger-700 mb-2">
              Error al cargar submissions
            </h2>
            <p className="text-danger-600 mb-4">{error}</p>
            <button
              onClick={refetch}
              className="bg-danger-600 hover:bg-danger-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Reintentar
            </button>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  const total = submissions.length;

  return (
    <ProtectedRoute>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {header}

        {submissions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay submissions aún</h3>
            <p className="text-gray-600 mb-6">
              Envía tu primera solución para verla aparecer aquí.
            </p>
            <a
              href="/submit"
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Enviar Código
            </a>
          </div>
        ) : (
          <>
            <SubmissionSummaryStats
              total={total}
              successfulDistinctExercises={successfulDistinct}
              progressEnabledPercent={progressEnabledPercent}
              progressCoursePercent={progressCoursePercent}
              solvedEnabledLabel={solvedEnabledLabel}
              solvedCourseLabel={solvedCourseLabel}
            />
            <SubmissionsList
              submissions={submissions}
              onSelectSubmission={setSelectedSubmission}
            />
          </>
        )}

        <SubmissionDetailModal
          submission={selectedSubmission}
          isOpen={selectedSubmission !== null}
          onClose={() => setSelectedSubmission(null)}
        />
      </div>
    </ProtectedRoute>
  );
}
